/**
 * Fund Type Validation Utilities
 * 
 * This module provides utilities for fund type validation and management
 * for TEFAS API requests.
 */

import { FundType } from '../types';
import { InvalidFundTypeError } from '../errors';

/**
 * Array of all supported fund types
 */
export const SUPPORTED_FUND_TYPES: readonly FundType[] = [
  FundType.EMK,
  FundType.YAT,
  FundType.BYF,
  FundType.GYF,
  FundType.GSYF
] as const;

/**
 * Fund type descriptions for user-friendly error messages
 */
export const FUND_TYPE_DESCRIPTIONS: Record<FundType, string> = {
  [FundType.EMK]: 'Emeklilik (Pension) funds',
  [FundType.YAT]: 'Yatırım (Investment) funds',
  [FundType.BYF]: 'Bireysel Yatırım (Individual Investment) funds',
  [FundType.GYF]: 'Gayrimenkul Yatırım (Real Estate Investment) funds',
  [FundType.GSYF]: 'Girişim Sermayesi Yatırım (Venture Capital Investment) funds'
};

/**
 * Validates if a value is a valid fund type
 * 
 * @param value - Value to validate
 * @returns True if valid fund type, false otherwise
 */
export function isValidFundType(value: unknown): value is FundType {
  return typeof value === 'string' && SUPPORTED_FUND_TYPES.includes(value as FundType);
}

/**
 * Validates a fund type and throws an error if invalid
 * 
 * @param fundType - Fund type to validate
 * @throws InvalidFundTypeError if fund type is invalid
 */
export function validateFundType(fundType: unknown): asserts fundType is FundType {
  if (!fundType || typeof fundType !== 'string') {
    throw new InvalidFundTypeError(String(fundType));
  }

  if (!isValidFundType(fundType)) {
    throw new InvalidFundTypeError(fundType);
  }
}

/**
 * Gets all supported fund types
 * 
 * @returns Array of supported fund types
 */
export function getSupportedFundTypes(): readonly FundType[] {
  return SUPPORTED_FUND_TYPES;
}

/**
 * Gets fund type description
 * 
 * @param fundType - Fund type to get description for
 * @returns Human-readable description of the fund type
 */
export function getFundTypeDescription(fundType: FundType): string {
  return FUND_TYPE_DESCRIPTIONS[fundType];
}

/**
 * Gets all fund type descriptions
 * 
 * @returns Object mapping fund types to their descriptions
 */
export function getAllFundTypeDescriptions(): Record<FundType, string> {
  return { ...FUND_TYPE_DESCRIPTIONS };
}

/**
 * Converts a fund type to its TEFAS API representation
 * 
 * @param fundType - Fund type to convert
 * @returns TEFAS API fund type string
 */
export function toTefasApiFundType(fundType: FundType): string {
  // TEFAS API uses the same fund type codes
  return fundType;
}

/**
 * Converts a TEFAS API fund type to our FundType enum
 * 
 * @param apiFundType - TEFAS API fund type string
 * @returns FundType enum value
 * @throws InvalidFundTypeError if API fund type is not supported
 */
export function fromTefasApiFundType(apiFundType: string): FundType {
  if (!isValidFundType(apiFundType)) {
    throw new InvalidFundTypeError(apiFundType);
  }
  return apiFundType as FundType;
}

/**
 * Checks if a fund type is supported
 * 
 * @param fundType - Fund type to check
 * @returns True if supported, false otherwise
 */
export function isSupportedFundType(fundType: string): boolean {
  return isValidFundType(fundType);
}

/**
 * Gets fund type validation error message
 * 
 * @param fundType - Invalid fund type
 * @returns User-friendly error message
 */
export function getFundTypeValidationError(fundType: string): string {
  const supportedTypes = SUPPORTED_FUND_TYPES.join(', ');
  return `Invalid fund type '${fundType}'. Supported types are: ${supportedTypes}`;
}

/**
 * Normalizes fund type string (uppercase, trimmed)
 * 
 * @param fundType - Fund type string to normalize
 * @returns Normalized fund type string
 */
export function normalizeFundType(fundType: string): string {
  return fundType.trim().toUpperCase();
}

/**
 * Validates and normalizes a fund type
 * 
 * @param fundType - Fund type to validate and normalize
 * @returns Normalized fund type
 * @throws InvalidFundTypeError if fund type is invalid
 */
export function validateAndNormalizeFundType(fundType: string): FundType {
  const normalized = normalizeFundType(fundType);
  validateFundType(normalized);
  return normalized as FundType;
}

/**
 * Gets fund type display name for UI purposes
 * 
 * @param fundType - Fund type to get display name for
 * @returns Display name for the fund type
 */
export function getFundTypeDisplayName(fundType: FundType): string {
  const descriptions = {
    [FundType.EMK]: 'Pension Funds',
    [FundType.YAT]: 'Investment Funds',
    [FundType.BYF]: 'Individual Investment Funds',
    [FundType.GYF]: 'Real Estate Investment Funds',
    [FundType.GSYF]: 'Venture Capital Investment Funds'
  };
  
  return descriptions[fundType];
}

/**
 * Gets fund type category for grouping purposes
 * 
 * @param fundType - Fund type to get category for
 * @returns Category of the fund type
 */
export function getFundTypeCategory(fundType: FundType): string {
  const categories = {
    [FundType.EMK]: 'Retirement',
    [FundType.YAT]: 'Investment',
    [FundType.BYF]: 'Individual',
    [FundType.GYF]: 'Real Estate',
    [FundType.GSYF]: 'Venture Capital'
  };
  
  return categories[fundType];
}
